<cnt-navbar></cnt-navbar>

<main class="pt-24 md:pt-28 min-h-screen bg-cream bg-grid-subtle pb-32 lg:pb-16">
  @if (loadingListing) {
    <section class="px-[2%] py-20">
      <div class="flex flex-col items-center gap-3 text-muted-text">
        <span class="material-symbols-outlined text-4xl animate-spin" aria-hidden="true">progress_activity</span>
        <span class="text-xs uppercase tracking-[0.12em] font-button font-bold">Loading your booking…</span>
      </div>
    </section>
  } @else if (!hasValidState) {
    <section class="px-[2%] py-20">
      <div class="max-w-md mx-auto text-center px-4">
        <span class="material-symbols-outlined text-5xl text-muted-text" aria-hidden="true">error</span>
        <h1 class="font-headline font-bold text-2xl mt-4 mb-2">Booking details missing</h1>
        <p class="text-muted-text text-sm font-body mb-6">
          We couldn't find that property or trip dates. Start over from the property page.
        </p>
        <a
          routerLink="/search"
          class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trinidad text-white text-xs uppercase tracking-[0.12em] font-button font-bold hover:opacity-95"
          >Browse stays</a
        >
      </div>
    </section>
  } @else {
    <!-- Progress steps strip -->
    <section class="px-[2%]">
      <div class="max-w-[80rem] mx-auto px-4 md:px-8 pt-6">
        <a
          [routerLink]="listingSlugRoute"
          class="inline-flex items-center gap-1.5 text-trinidad text-xs uppercase tracking-[0.12em] font-button font-bold mb-6 hover:underline"
        >
          <span class="material-symbols-outlined text-base" aria-hidden="true">arrow_back</span>
          Back to property
        </a>
        <ol
          class="flex items-center gap-3 text-[0.65rem] uppercase tracking-[0.14em] font-button font-bold mb-2 overflow-x-auto"
        >
          <li class="inline-flex items-center gap-2 text-jungle-green">
            <span class="w-6 h-6 rounded-full bg-jungle-green text-white inline-flex items-center justify-center"
              ><span aria-hidden="true" class="material-symbols-outlined text-[14px]">check</span></span
            >
            Choose dates
          </li>
          <span class="w-8 h-px bg-dark-text/20"></span>
          <li class="inline-flex items-center gap-2 text-trinidad">
            <span
              class="w-6 h-6 rounded-full bg-trinidad text-white inline-flex items-center justify-center text-[11px]"
              >2</span
            >
            Review &amp; confirm
          </li>
          <span class="w-8 h-px bg-dark-text/15"></span>
          <li class="inline-flex items-center gap-2 text-muted-text/60">
            <span
              class="w-6 h-6 rounded-full bg-dark-text/10 text-muted-text/70 inline-flex items-center justify-center text-[11px]"
              >3</span
            >
            Confirmed
          </li>
        </ol>
      </div>
    </section>

    <section class="px-[2%]">
      <div class="max-w-[80rem] mx-auto px-4 md:px-8 pt-6">
        <h1
          class="review-anim font-headline font-bold text-dark-text tracking-tight leading-[1.05] text-4xl md:text-5xl"
        >
          Almost there.
        </h1>
        <p class="review-anim text-muted-text text-base font-body mt-2">
          Take one last look — we'll lock everything in once you confirm.
        </p>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 mt-10">
          <!-- Left: trip + contact + payment + terms -->
          <div class="lg:col-span-7 space-y-6">
            <!-- Trip summary card with edit-in-place -->
            <div
              class="review-anim bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6 md:p-8"
            >
              <div class="flex items-start justify-between gap-4 mb-5">
                <div>
                  <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-1"
                    >Your trip</span
                  >
                  <h2 class="font-headline font-bold text-dark-text text-xl md:text-2xl leading-tight">
                    {{ datesLabel }}
                  </h2>
                  <p class="text-xs text-muted-text font-body mt-1">
                    {{ nights }} {{ nights === 1 ? 'night' : 'nights' }} · {{ guests }}
                    {{ guests === 1 ? 'guest' : 'guests' }}
                  </p>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-dark-text/8">
                <!-- Dates editor -->
                <div class="relative">
                  <button
                    type="button"
                    (click)="toggleEditor('dates')"
                    class="w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors"
                    [ngClass]="
                      openEditor === 'dates'
                        ? 'border-trinidad bg-trinidad/5'
                        : 'border-dark-text/8 hover:border-trinidad/40'
                    "
                  >
                    <span aria-hidden="true" class="material-symbols-outlined text-jungle-green">calendar_today</span>
                    <div class="flex-1 min-w-0">
                      <div class="text-[0.65rem] font-label uppercase tracking-[0.1em] text-muted-text font-bold">
                        Dates
                      </div>
                      <div class="text-sm font-body font-bold text-dark-text truncate">
                        {{ checkInLabel }} → {{ checkOutLabel }}
                      </div>
                    </div>
                    <span aria-hidden="true" class="material-symbols-outlined text-base text-trinidad">edit</span>
                  </button>
                  @if (openEditor === 'dates') {
                    <div class="fixed inset-0 z-20" (click)="closeEditor()"></div>
                    <div
                      role="dialog"
                      aria-label="Edit dates"
                      cntFocusTrap
                      (escape)="closeEditor()"
                      class="review-popover"
                    >
                      <div class="flex items-center justify-between mb-2 px-1">
                        <span class="text-[0.7rem] font-label uppercase tracking-[0.14em] text-dark-text font-bold"
                          >Pick dates</span
                        >
                        <button
                          type="button"
                          (click)="closeEditor()"
                          class="text-trinidad text-xs uppercase tracking-[0.12em] font-button font-bold hover:underline"
                        >
                          Done
                        </button>
                      </div>
                      <mat-calendar
                        [selected]="selectedDateRange"
                        [minDate]="minDate"
                        [maxDate]="maxDate"
                        [dateFilter]="dateFilter"
                        (selectedChange)="onDateSelected($event)"
                      ></mat-calendar>
                    </div>
                  }
                </div>

                <!-- Guests editor -->
                <div class="relative">
                  <button
                    type="button"
                    (click)="toggleEditor('guests')"
                    class="w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors"
                    [ngClass]="
                      openEditor === 'guests'
                        ? 'border-trinidad bg-trinidad/5'
                        : 'border-dark-text/8 hover:border-trinidad/40'
                    "
                  >
                    <span aria-hidden="true" class="material-symbols-outlined text-jungle-green">groups</span>
                    <div class="flex-1 min-w-0">
                      <div class="text-[0.65rem] font-label uppercase tracking-[0.1em] text-muted-text font-bold">
                        Guests
                      </div>
                      <div class="text-sm font-body font-bold text-dark-text">
                        {{ guests }} {{ guests === 1 ? 'guest' : 'guests' }}
                      </div>
                    </div>
                    <span aria-hidden="true" class="material-symbols-outlined text-base text-trinidad">edit</span>
                  </button>
                  @if (openEditor === 'guests') {
                    <div class="fixed inset-0 z-20" (click)="closeEditor()"></div>
                    <div
                      role="dialog"
                      aria-label="Edit guests"
                      cntFocusTrap
                      (escape)="closeEditor()"
                      class="review-popover"
                      style="min-width: 16rem"
                    >
                      <div class="flex items-center justify-between mb-3">
                        <span class="text-[0.7rem] font-label uppercase tracking-[0.14em] text-dark-text font-bold"
                          >Guests</span
                        >
                        <span class="text-[0.65rem] text-muted-text font-body">Max {{ detail!.maxGuests }}</span>
                      </div>
                      <div class="flex items-center justify-between gap-4">
                        <div class="text-sm font-body text-dark-text">Total guests</div>
                        <div class="flex items-center gap-3">
                          <button aria-label="Remove a guest"
                            type="button"
                            (click)="adjustGuests(-1)"
                            [disabled]="guests <= 1"
                            class="w-10 h-10 rounded-full border border-dark-text/15 flex items-center justify-center hover:border-trinidad disabled:opacity-40"
                          >
                            <span aria-hidden="true" class="material-symbols-outlined text-base">remove</span>
                          </button>
                          <span class="font-headline font-bold text-dark-text text-lg w-6 text-center">{{
                            guests
                          }}</span>
                          <button
                            type="button"
                            (click)="adjustGuests(1)"
                            [disabled]="guests >= detail!.maxGuests"
                            class="w-10 h-10 rounded-full border border-dark-text/15 flex items-center justify-center hover:border-trinidad disabled:opacity-40"
                          >
                            <span aria-hidden="true" class="material-symbols-outlined text-base">add</span>
                          </button>
                        </div>
                      </div>
                      <button
                        type="button"
                        (click)="closeEditor()"
                        class="w-full mt-4 px-4 py-2.5 rounded-full bg-trinidad text-white text-xs uppercase tracking-[0.12em] font-button font-bold hover:opacity-95"
                      >
                        Done
                      </button>
                    </div>
                  }
                </div>

                <!-- RV picker — which saved rig is this booking for? -->
                @if (rvProfiles.length > 1) {
                  <div>
                    <div class="text-[0.65rem] font-label uppercase tracking-[0.1em] text-muted-text font-bold mb-2">
                      Booking with
                    </div>
                    <div class="flex flex-wrap gap-2" role="radiogroup" aria-label="Which RV are you bringing?">
                      @for (p of rvProfiles; track p.syncId) {
                        <button
                          type="button"
                          (click)="selectRv(p.syncId!)"
                          role="radio"
                          [attr.aria-checked]="p.syncId === selectedRvId"
                          [ngClass]="
                            p.syncId === selectedRvId
                              ? 'border-trinidad bg-trinidad/10 text-trinidad'
                              : 'border-dark-text/15 bg-white text-dark-text hover:border-trinidad/40'
                          "
                          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-body font-bold transition-colors"
                        >
                          @if (p.syncId === selectedRvId) {
                            <span aria-hidden="true" class="material-symbols-outlined text-base" style="font-variation-settings: 'FILL' 1"
                              >check_circle</span
                            >
                          }
                          {{ p.name }}
                        </button>
                      }
                    </div>
                  </div>
                }

                <!-- Rig (read-only — link back to /account#rig if user wants to edit) -->
                <a
                  routerLink="/user-dashboard/user-profile"
                  [queryParams]="selectedRvId ? { rv: selectedRvId } : null"
                  fragment="rig"
                  class="flex items-center gap-3 p-3 rounded-xl border transition-colors no-underline"
                  [ngClass]="
                    isMyRvComplete ? 'border-dark-text/8 hover:border-trinidad/40' : 'border-trinidad/40 bg-trinidad/5'
                  "
                >
                  <span aria-hidden="true"
                    class="material-symbols-outlined"
                    [ngClass]="isMyRvComplete ? 'text-jungle-green' : 'text-trinidad'"
                    >rv_hookup</span
                  >
                  <div class="flex-1 min-w-0">
                    <div class="text-[0.65rem] font-label uppercase tracking-[0.1em] text-muted-text font-bold">
                      Your rig
                    </div>
                    @if (isMyRvComplete) {
                      <div class="text-sm font-body font-bold text-dark-text truncate">{{ rvSummary }}</div>
                    } @else {
                      <div class="text-sm font-body font-bold text-dark-text">
                        Add your {{ rigMissingLabel }} to book.
                      </div>
                    }
                  </div>
                  <span aria-hidden="true" class="material-symbols-outlined text-base text-trinidad">arrow_forward</span>
                </a>
              </div>
            </div>

            <!-- Contact card -->
            <div
              class="review-anim bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6 md:p-8"
            >
              <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-1"
                >Contact</span
              >
              <h2 class="font-headline font-bold text-dark-text text-xl md:text-2xl leading-tight mb-1">
                How {{ detail!.host.name.split(' ')[0] }} can reach you
              </h2>
              <p class="text-xs text-muted-text font-body mb-5">Used only for trip-related messages.</p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label class="flex flex-col gap-2">
                  <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text font-bold">Email</span>
                  <input
                    type="email" autocomplete="email"
                    name="contactEmail"
                    required
                    [(ngModel)]="contactEmail"
                    (blur)="validateEmail()"
                    (input)="emailTouched && validateEmail()"
                    [attr.aria-invalid]="!!fieldErrors.email"
                    [class.border-trinidad]="!!fieldErrors.email"
                    class="bg-cream/60 border border-dark-text/15 rounded-xl px-4 py-3.5 text-sm font-body text-dark-text focus:outline-none focus:border-jungle-green focus:ring-2 focus:ring-jungle-green/15 transition-all"
                  />
                  @if (fieldErrors.email) {
                    <span class="text-[0.7rem] text-trinidad font-body inline-flex items-center gap-1"
                      ><span aria-hidden="true" class="material-symbols-outlined text-[14px]">error</span>{{ fieldErrors.email }}</span
                    >
                  }
                </label>
                <label class="flex flex-col gap-2">
                  <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text font-bold"
                    >Phone <span class="text-muted-text/70 normal-case font-normal">(optional)</span></span
                  >
                  <input
                    type="tel" autocomplete="tel"
                    name="contactPhone"
                    [(ngModel)]="contactPhone"
                    cntMaskPhone
                    (blur)="validatePhone()"
                    placeholder="(555) 123-4567"
                    [attr.aria-invalid]="!!fieldErrors.phone"
                    [class.border-trinidad]="!!fieldErrors.phone"
                    class="bg-cream/60 border border-dark-text/15 rounded-xl px-4 py-3.5 text-sm font-body text-dark-text focus:outline-none focus:border-jungle-green focus:ring-2 focus:ring-jungle-green/15 transition-all"
                  />
                  @if (fieldErrors.phone) {
                    <span class="text-[0.7rem] text-trinidad font-body inline-flex items-center gap-1"
                      ><span aria-hidden="true" class="material-symbols-outlined text-[14px]">error</span>{{ fieldErrors.phone }}</span
                    >
                  }
                </label>
              </div>
            </div>

            <!-- Note to host (request-to-book only) -->
            @if (!(listing!.reservationProcess === 'instant')) {
              <div
                class="review-anim bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6 md:p-8"
              >
                <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-1"
                  >Note to host</span
                >
                <h2 class="font-headline font-bold text-dark-text text-xl md:text-2xl leading-tight mb-1">
                  Introduce yourself
                  <span class="text-muted-text/70 normal-case font-normal text-base">(optional)</span>
                </h2>
                <p class="text-xs text-muted-text font-body mb-4">
                  A friendly hello helps your request stand out. Mention your rig, plans, or any questions.
                </p>
                <textarea aria-label="Message to the host (optional)"
                  [(ngModel)]="noteToHost"
                  name="noteToHost"
                  rows="4"
                  maxlength="500"
                  placeholder="Hi! We're a couple traveling with a 28-ft Class C and would love to stay…"
                  class="w-full bg-cream/60 border border-dark-text/15 rounded-md px-4 py-3.5 text-sm font-body text-dark-text focus:outline-none focus:border-jungle-green focus:ring-2 focus:ring-jungle-green/15 transition-all resize-y"
                ></textarea>
                <div class="text-[0.65rem] text-muted-text/70 font-body text-right mt-1">
                  {{ noteToHost.length }}/500
                </div>
              </div>
            }

            <!-- Add-ons -->
            @if (detail!.addOns.length) {
              <div
                class="review-anim bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6 md:p-8"
              >
                <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-1"
                  >Add-ons</span
                >
                <h2 class="font-headline font-bold text-dark-text text-xl md:text-2xl leading-tight mb-1">
                  Make it extra
                </h2>
                <p class="text-xs text-muted-text font-body mb-5">
                  Optional extras from {{ detail!.host.name.split(' ')[0] }}. Add now, edit later.
                </p>
                <ul class="space-y-2">
                  @for (a of detail!.addOns; track a.id) {
                    <li>
                      <button
                        type="button"
                        (click)="toggleAddOn(a.id)"
                        [attr.aria-pressed]="isAddOnSelected(a.id)"
                        [attr.aria-label]="(isAddOnSelected(a.id) ? 'Remove ' : 'Add ') + a.label"
                        class="w-full text-left flex items-center gap-3 p-4 rounded-md border transition-colors"
                        [ngClass]="
                          isAddOnSelected(a.id)
                            ? 'border-trinidad bg-trinidad/5'
                            : 'border-dark-text/10 hover:border-trinidad/40'
                        "
                      >
                        <span aria-hidden="true" class="material-symbols-outlined text-jungle-green shrink-0">{{ a.icon }}</span>
                        <div class="flex-1 min-w-0">
                          <div class="text-sm font-body font-bold text-dark-text">{{ a.label }}</div>
                          <div class="text-xs text-muted-text font-body line-clamp-1">{{ a.description }}</div>
                        </div>
                        <div class="text-right shrink-0">
                          <div class="text-sm font-headline font-bold text-trinidad">${{ a.price }}</div>
                          <div class="text-[0.6rem] uppercase tracking-[0.1em] font-button font-bold text-muted-text">
                            {{ a.unit }}
                          </div>
                        </div>
                        <span aria-hidden="true"
                          class="material-symbols-outlined text-base shrink-0"
                          [ngClass]="isAddOnSelected(a.id) ? 'text-trinidad' : 'text-muted-text/40'"
                          [style.font-variation-settings]="isAddOnSelected(a.id) ? '\'FILL\' 1' : null"
                        >
                          {{ isAddOnSelected(a.id) ? 'check_circle' : 'add_circle' }}
                        </span>
                      </button>
                      @if (isAddOnSelected(a.id)) {
                        <cnt-add-on-qty-stepper
                          class="block mt-2 ml-4"
                          [qty]="addOnQty(a.id)"
                          [max]="addOnMax(a)"
                          [label]="a.label"
                          (step)="adjustAddOnQty(a.id, $event)"
                        >
                          <span class="text-xs font-body text-muted-text">
                            = <span class="font-bold text-dark-text">{{ addOnLineTotal(a) | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                            @if (a.maxQuantity && a.maxQuantity > 1) {
                              <span class="ml-1">(max {{ a.maxQuantity }})</span>
                            }
                          </span>
                        </cnt-add-on-qty-stepper>
                      }
                    </li>
                  }
                </ul>
              </div>
            }

            <!-- Payment method -->
            <div
              class="review-anim bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6 md:p-8"
            >
              <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-1"
                >Pay with</span
              >
              <h2 class="font-headline font-bold text-dark-text text-xl md:text-2xl leading-tight mb-1">
                Saved payment
              </h2>
              <p class="text-xs text-muted-text font-body mb-5">
                You'll be charged on <span class="font-bold text-dark-text">{{ chargeDate }}</span
                >. No charge today.
              </p>
              <div class="space-y-2">
                @if (paymentMethods.length === 0 && !addCardOpen) {
                  <div class="rounded-xl bg-cream/40 border border-dark-text/8 px-5 py-8 text-center mb-1">
                    <span class="material-symbols-outlined text-3xl text-muted-text" aria-hidden="true"
                      >credit_card</span
                    >
                    <h3 class="font-headline font-bold text-dark-text text-base mt-2 mb-1">No payment methods</h3>
                    <p class="text-sm text-muted-text font-body">Add a card to complete this booking.</p>
                  </div>
                }
                @for (pm of paymentMethods; track pm.id) {
                  <div
                    class="flex flex-wrap items-center gap-3 p-4 rounded-xl border transition-colors"
                    [ngClass]="
                      selectedPaymentId === pm.id
                        ? 'border-trinidad bg-trinidad/5'
                        : 'border-dark-text/8 hover:border-trinidad/40'
                    "
                  >
                    <label class="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                      <input
                        type="radio"
                        name="payment"
                        [value]="pm.id"
                        [(ngModel)]="selectedPaymentId"
                        class="accent-trinidad"
                      />
                      <span aria-hidden="true" class="material-symbols-outlined text-jungle-green">{{ pm.icon }}</span>
                      <span class="text-sm font-body font-bold text-dark-text flex-1">{{ pm.label }}</span>
                      @if (pm.isDefault) {
                        <span class="text-[0.6rem] font-button uppercase tracking-[0.1em] font-bold text-muted-text"
                          >Default</span
                        >
                      }
                    </label>
                    @if (pm.isReal) {
                      <button
                        type="button"
                        (click)="replaceCard(pm.id)"
                        class="text-[0.65rem] uppercase tracking-[0.12em] font-button font-bold text-trinidad hover:underline"
                      >
                        Replace
                      </button>
                      <button
                        type="button"
                        (click)="removeCard(pm.id)"
                        class="shrink-0 w-9 h-9 rounded-full border border-dark-text/15 inline-flex items-center justify-center hover:border-trinidad hover:text-trinidad transition-colors"
                        aria-label="Remove card"
                      >
                        <span aria-hidden="true" class="material-symbols-outlined text-base">delete</span>
                      </button>
                    }
                  </div>
                  @if (pm.isReal && pm.isActive === false) {
                    <p class="text-xs text-trinidad font-body -mt-1 px-1">
                      This card is no longer valid — please replace it before booking.
                    </p>
                  }
                }
                @if (addCardOpen) {
                  @if (isRealListing) {
                  <div class="p-4 rounded-xl bg-cream/40 border border-dark-text/8">
                    <div class="text-sm font-body font-bold text-dark-text mb-1">Add a card</div>
                    <p class="text-xs text-muted-text font-body mb-3">
                      This is a live property — booking it charges a real (test-mode) card on file.
                    </p>
                    <div class="grid grid-cols-2 gap-3">
                      <label class="flex flex-col gap-1 col-span-2">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text"
                          >Card number</span
                        >
                        <input
                          type="text"
                          inputmode="numeric"
                          autocomplete="cc-number"
                          name="realCardNumber"
                          [(ngModel)]="realCardNumber"
                          cntMaskCCNum
                          placeholder="4111 1111 1111 1111"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        />
                      </label>
                      <label class="flex flex-col gap-1">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text"
                          >Exp month (MM)</span
                        >
                        <input
                          type="text"
                          inputmode="numeric"
                          maxlength="2"
                          autocomplete="cc-exp-month"
                          name="realCardExpMonth"
                          [(ngModel)]="realCardExpMonth"
                          placeholder="04"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        />
                      </label>
                      <label class="flex flex-col gap-1">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text"
                          >Exp year (YYYY)</span
                        >
                        <input
                          type="text"
                          inputmode="numeric"
                          maxlength="4"
                          autocomplete="cc-exp-year"
                          name="realCardExpYear"
                          [(ngModel)]="realCardExpYear"
                          placeholder="2028"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        />
                      </label>
                      <label class="flex flex-col gap-1">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text">CVV</span>
                        <input
                          type="text"
                          inputmode="numeric"
                          maxlength="4"
                          autocomplete="cc-csc"
                          name="realCardCvv"
                          [(ngModel)]="realCardCvv"
                          cntMaskCCCvc
                          placeholder="123"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        />
                      </label>
                      <label class="flex flex-col gap-1">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text"
                          >Billing zip</span
                        >
                        <input
                          type="text"
                          inputmode="numeric"
                          maxlength="5"
                          autocomplete="postal-code"
                          name="realCardZip"
                          [(ngModel)]="realCardZip"
                          placeholder="97201"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        />
                      </label>
                      <label class="flex flex-col gap-1 col-span-2">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text"
                          >Billing address</span
                        >
                        <input
                          type="text"
                          autocomplete="address-line1"
                          name="realCardAddress"
                          [(ngModel)]="realCardAddress"
                          placeholder="123 Main St"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        />
                      </label>
                      <label class="flex flex-col gap-1">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text">City</span>
                        <input
                          type="text"
                          autocomplete="address-level2"
                          name="realCardCity"
                          [(ngModel)]="realCardCity"
                          placeholder="Portland"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        />
                      </label>
                      <label class="flex flex-col gap-1">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text">State</span>
                        <input
                          type="text"
                          autocomplete="address-level1"
                          name="realCardState"
                          [(ngModel)]="realCardState"
                          placeholder="OR"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        />
                      </label>
                    </div>
                    @if (realCardError) {
                      <p role="alert" class="text-xs text-trinidad font-body mt-2">{{ realCardError }}</p>
                    }
                    <div class="flex justify-end gap-2 mt-3">
                      <button
                        type="button"
                        (click)="cancelAddCard()"
                        [disabled]="savingRealCard"
                        class="px-5 py-2.5 rounded-full bg-white border border-dark-text/15 text-dark-text text-[0.65rem] uppercase tracking-[0.12em] font-button font-bold hover:border-dark-text disabled:opacity-40"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        (click)="addRealCard()"
                        [disabled]="!canAddRealCard || savingRealCard"
                        class="px-5 py-2.5 rounded-full bg-trinidad text-white text-[0.65rem] uppercase tracking-[0.12em] font-button font-bold hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {{ savingRealCard ? 'Saving…' : 'Add card' }}
                      </button>
                    </div>
                  </div>
                } @else {
                  <div class="p-4 rounded-xl bg-cream/40 border border-dark-text/8">
                    <div class="text-sm font-body font-bold text-dark-text mb-3">Add a card</div>
                    <div class="grid grid-cols-2 gap-3">
                      <label class="flex flex-col gap-1">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text">Brand</span>
                        <select
                          name="newCardBrand"
                          [(ngModel)]="newCardBrand"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        >
                          <option value="visa">Visa</option>
                          <option value="mastercard">Mastercard</option>
                          <option value="amex">Amex</option>
                          <option value="discover">Discover</option>
                        </select>
                      </label>
                      <label class="flex flex-col gap-1">
                        <span class="text-xs font-label uppercase tracking-[0.1em] text-muted-text">Last 4</span>
                        <input
                          type="text"
                          inputmode="numeric"
                          maxlength="4"
                          name="newCardLast4"
                          [(ngModel)]="newCardLast4"
                          placeholder="4242"
                          class="bg-white border border-dark-text/15 rounded-xl px-3 py-2.5 text-sm font-body"
                        />
                      </label>
                    </div>
                    <div class="flex justify-end gap-2 mt-3">
                      <button
                        type="button"
                        (click)="cancelAddCard()"
                        class="px-5 py-2.5 rounded-full bg-white border border-dark-text/15 text-dark-text text-[0.65rem] uppercase tracking-[0.12em] font-button font-bold hover:border-dark-text"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        (click)="addCard()"
                        [disabled]="!canAddCard"
                        class="px-5 py-2.5 rounded-full bg-trinidad text-white text-[0.65rem] uppercase tracking-[0.12em] font-button font-bold hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Add card
                      </button>
                    </div>
                  </div>
                  }
                } @else if (!isRealListing || !hasRealCard) {
                  <button
                    type="button"
                    (click)="addCardOpen = true"
                    class="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-dark-text/20 text-muted-text text-xs uppercase tracking-[0.12em] font-button font-bold hover:border-trinidad hover:text-trinidad transition-colors"
                  >
                    <span aria-hidden="true" class="material-symbols-outlined text-base">add</span>
                    {{ paymentMethods.length === 0 ? 'Add a card' : 'Add another card' }}
                  </button>
                }
              </div>
            </div>

            <!-- Booking policy + terms -->
            <div
              class="review-anim bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6 md:p-8"
            >
              <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-1"
                >Booking policy</span
              >
              <h2 class="font-headline font-bold text-dark-text text-xl md:text-2xl leading-tight mb-5">
                {{ listing!.reservationProcess === 'instant' ? 'Instant Book' : 'Request to book' }}
              </h2>
              <ul class="text-sm text-dark-text font-body leading-relaxed space-y-3 mb-6">
                <li class="flex gap-3">
                  <span aria-hidden="true"
                    class="material-symbols-outlined text-base text-jungle-green shrink-0 mt-0.5"
                    style="font-variation-settings: 'FILL' 1"
                    >check_circle</span
                  >
                  <span>{{
                    listing!.reservationProcess === 'instant'
                      ? 'Your reservation confirms immediately — no waiting on the host.'
                      : 'The host has 24 hours to approve your request.'
                  }}</span>
                </li>
                <li class="flex gap-3">
                  <span aria-hidden="true"
                    class="material-symbols-outlined text-base text-jungle-green shrink-0 mt-0.5"
                    style="font-variation-settings: 'FILL' 1"
                    >check_circle</span
                  >
                  <span>You'll receive the exact arrival address as soon as the booking is confirmed.</span>
                </li>
              </ul>

              <!-- Cancellation timeline — same segment shape as /listing/:id
                   so the trust signal stays coherent across the funnel. -->
              @if (cancellationSegments.length > 0) {
                <div class="mb-5" role="group" aria-label="Cancellation policy timeline">
                  <div
                    class="text-[0.65rem] font-label uppercase tracking-[0.12em] text-muted-text font-bold mb-2"
                    id="booking-cancellation-policy-label"
                  >
                    Cancellation policy
                  </div>
                  <div class="flex flex-col sm:flex-row gap-2">
                    @for (seg of cancellationSegments; track seg.label) {
                      <div
                        class="flex-1 rounded-md border px-3 py-2"
                        [attr.aria-label]="seg.label + (seg.dateLabel ? ' until ' + seg.dateLabel : ' (from then on)')"
                        [ngClass]="
                          seg.tone === 'good'
                            ? 'border-jungle-green/40 bg-jungle-green/5'
                            : seg.tone === 'warn'
                              ? 'border-gold/40 bg-gold/5'
                              : 'border-trinidad/40 bg-trinidad/5'
                        "
                      >
                        <div
                          class="text-xs uppercase tracking-[0.12em] font-button font-bold"
                          [ngClass]="
                            seg.tone === 'good'
                              ? 'text-jungle-green'
                              : seg.tone === 'warn'
                                ? 'text-dark-text'
                                : 'text-trinidad'
                          "
                        >
                          {{ seg.label }}
                        </div>
                        @if (seg.dateLabel) {
                          <div class="text-sm font-body font-bold text-dark-text mt-0.5">until {{ seg.dateLabel }}</div>
                        } @else {
                          <div class="text-xs font-body text-muted-text mt-0.5 italic">from then on</div>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
              <label
                class="flex items-start gap-3 cursor-pointer p-4 rounded-xl bg-cream/60 border border-dark-text/8 hover:border-trinidad/40 transition-colors"
              >
                <input
                  type="checkbox"
                  [(ngModel)]="acceptedTerms"
                  name="terms"
                  class="mt-1 w-4 h-4 accent-trinidad cursor-pointer"
                />
                <span class="text-sm text-dark-text font-body"
                  >I agree to the
                  <a routerLink="/terms" class="text-trinidad font-bold hover:underline">booking terms</a> and the
                  host's house rules.</span
                >
              </label>
            </div>

            @if (error) {
              <!-- P58/B — aria-live assertive so screen readers announce
                   async errors immediately; icon hidden since the copy
                   already starts with "Error:"-grade context. -->
              <div
                class="rounded-xl bg-trinidad/5 border border-trinidad/30 p-4 text-trinidad text-sm font-body inline-flex items-center gap-2"
                role="alert"
                aria-live="assertive"
              >
                <span class="material-symbols-outlined text-base" aria-hidden="true">error</span>
                {{ error }}
              </div>
            }

            <!-- Pre-CTA hint when the user hasn't verified yet — softens the modal interruption
                 that fires after Confirm. Names the duration and pre-empts privacy concerns. -->
            <!-- todo: add back when we have identity verification -->
            <!-- @if (user && !user.verified) {
              <div class="mb-3 flex items-start gap-2.5 p-3 rounded-lg bg-jungle-green/8 border border-jungle-green/25">
                <span aria-hidden="true"
                  class="material-symbols-outlined text-base shrink-0 mt-0.5"
                  style="color: #1f4933; font-variation-settings: 'FILL' 1"
                  >verified_user</span
                >
                <p class="text-xs font-body leading-relaxed" style="color: #1f4933">
                  <strong>First booking</strong> — we'll ask for a quick ID check (~30 seconds, one-time) before locking
                  it in. Your document is encrypted and never shared with the host.
                </p>
              </div>
            } -->

            <!-- In-form confirm CTA (always visible) -->
            <button
              type="button"
              (click)="onConfirm($event)"
              [disabled]="submitting || !isFormValid"
              class="flex w-full items-center justify-center gap-2 px-6 py-5 rounded-full bg-trinidad text-white font-button uppercase tracking-[0.12em] text-sm font-bold hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity shadow-[0_12px_28px_rgba(227,83,13,0.3)]"
            >
              @if (submitting) {
                <span aria-hidden="true" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                Locking it in…
              } @else {
                @if (!isFormValid) {
                  <span aria-hidden="true" class="material-symbols-outlined text-base">lock</span>
                }
                {{ listing!.reservationProcess === 'instant' ? 'Confirm & reserve' : 'Send request' }}
                <span aria-hidden="true" class="material-symbols-outlined text-base">arrow_forward</span>
              }
            </button>
            <p class="text-xs text-muted-text/70 font-body text-center">
              No charges yet — payment will be collected separately.
            </p>
          </div>

          <!-- Right: sticky listing recap + price breakdown + trust strip. Below lg
               the page is one column, so it comes first: guests see what they're
               booking and the price before the form and the Confirm button. -->
          <aside class="lg:col-span-5 order-first lg:order-none">
            <div class="lg:sticky lg:top-32 space-y-5">
              <div
                class="review-anim bg-white rounded-2xl border border-dark-text/8 shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden"
              >
                <div class="relative">
                  <img [src]="detail!.photos[0]" [alt]="listing!.title" class="w-full h-40 sm:h-48 lg:h-56 object-cover" />
                  @if (listing!.reservationProcess === 'instant') {
                    <span
                      class="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 bg-trinidad text-white rounded-full shadow-md"
                    >
                      <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1"
                        >bolt</span
                      >
                      <span class="text-[0.6rem] font-button uppercase tracking-[0.1em] font-bold">Instant Book</span>
                    </span>
                  }
                </div>
                <div class="p-5">
                  <span class="text-muted-text text-[0.65rem] font-label uppercase tracking-[0.12em] font-bold">{{
                    listing!.location
                  }}</span>
                  <h3 class="font-headline font-bold text-dark-text text-lg leading-tight line-clamp-2 mt-1">
                    {{ listing!.title }}
                  </h3>
                  <!-- Host strip — mirrors /listing/:id so the guest sees who
                       they're paying before checkout (P2.3 / C). -->
                  <div class="flex items-center gap-3 mt-3 pt-3 border-t border-dark-text/8">
                    <div
                      class="relative shrink-0 w-10 h-10 rounded-full overflow-hidden ring-2 ring-white shadow-sm bg-jungle-green/15"
                    >
                      @if (detail!.host.avatar) {
                        <img [src]="detail!.host.avatar" [alt]="detail!.host.name" class="w-full h-full object-cover" />
                      } @else {
                        <div
                          class="w-full h-full flex items-center justify-center text-[11px] font-headline font-bold text-jungle-green"
                        >
                          {{ detail!.host.initials }}
                        </div>
                      }
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <span class="text-sm font-body font-bold text-dark-text"
                          >Hosted by {{ detail!.host.name }}</span
                        >
                        <!-- @if (detail!.trustBadges.includes('verified-host')) {
                          <span
                            class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-jungle-green/10 text-jungle-green text-[0.55rem] font-button uppercase tracking-[0.1em] font-bold"
                          >
                            <span aria-hidden="true"
                              class="material-symbols-outlined text-[10px]"
                              style="font-variation-settings: 'FILL' 1"
                              >verified</span
                            >
                            Verified
                          </span>
                        } -->
                      </div>
                      <div class="text-[0.7rem] text-muted-text font-body">
                        Replies within {{ detail!.host.responseHours }}h
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Price details -->
              <div
                class="review-anim bg-white rounded-2xl border border-dark-text/8 shadow-[0_4px_16px_rgba(0,0,0,0.03)] p-6"
              >
                <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold block mb-4"
                  >Price details</span
                >
                <div class="flex flex-col gap-3 text-sm font-body text-dark-text">
                  <div class="flex justify-between">
                    <span>
                      @if (nightlyPriceRange.min === nightlyPriceRange.max) {
                        ${{ nightlyPriceRange.min }} × {{ nights }} {{ nights === 1 ? 'night' : 'nights' }}
                      } @else {
                        ${{ nightlyPriceRange.min }}–${{ nightlyPriceRange.max }} × {{ nights }} {{ nights === 1 ? 'night' : 'nights' }}
                      }
                    </span>
                    <span>{{ subtotal | currency: 'USD' : 'symbol' : '1.2-2'  }}</span>
                  </div>
                  @if (weeklyDiscount > 0) {
                    <div class="flex justify-between text-jungle-green">
                      <span class="inline-flex items-center gap-1"
                        >Weekly discount
                        <span
                          class="text-[0.65rem] font-button uppercase tracking-[0.1em] font-bold bg-jungle-green/10 px-1.5 py-0.5 rounded"
                          >10% off</span
                        > </span
                      ><span>−{{ weeklyDiscount | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                    </div>
                  }
                  @if (selectedAddOns.length > 0) {
                    @for (a of selectedAddOns; track a.id) {
                      <div class="flex justify-between text-muted-text">
                        <span class="truncate pr-2">
                          + {{ a.label }}@if (addOnQty(a.id) > 1) { × {{ addOnQty(a.id) }}}
                          @if (a.unit === 'per night' && nights > 1) {
                            <span class="text-[0.7rem] opacity-80"> · ${{ a.price }} × {{ nights }} nights</span>
                          } @else if (a.unit === 'per person' && guests > 1) {
                            <span class="text-[0.7rem] opacity-80"> · ${{ a.price }} × {{ guests }} guests</span>
                          } @else if (addOnQty(a.id) > 1) {
                            <span class="text-[0.7rem] opacity-80"> · ${{ a.price }} each</span>
                          }
                        </span>
                        <span>{{ addOnLineTotal(a) | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                      </div>
                    }
                  }
                  <!-- "Included" line — feedback incentive is baked into the nightly rate (all-in pricing). -->
                  @if (feedbackIncentive > 0) {
                    <div class="text-[0.7rem] text-muted-text -mt-1 pl-4">
                      Includes ${{ feedbackIncentive }} feedback incentive · refunds as CurbNTurf Cash after your review
                    </div>
                  }
                  @if (taxTotal > 0) {
                    <div class="flex justify-between">
                      <span>Taxes</span>
                      <span>{{ taxTotal | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                    </div>
                  }
                  @if (promoApplied) {
                    <div class="flex justify-between text-jungle-green">
                      <span class="inline-flex items-center gap-1"
                        >{{ promoApplied.code }}
                        <button
                          type="button"
                          (click)="removePromo()"
                          class="inline-flex items-center justify-center w-11 h-11 -my-3 -mx-2 text-trinidad/70 hover:text-trinidad"
                          aria-label="Remove promo"
                        >
                          <span aria-hidden="true" class="material-symbols-outlined text-[14px]">close</span>
                        </button>
                      </span>
                      <span>−{{ promoApplied.amount | currency: 'USD' : 'symbol' : '1.2-2'  }}</span>
                    </div>
                  }
                  @if (availableCredit > 0 && isRealListing) {
                    <div class="flex justify-between text-trinidad">
                      <span class="inline-flex items-center gap-1.5">
                        <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1"
                          >redeem</span
                        >
                        CurbNTurf Cash
                      </span>
                      <span>{{ availableCredit | currency: 'USD' : 'symbol' : '1.2-2'  }} available</span>
                    </div>
                    <p class="text-[0.65rem] text-muted-text font-body">
                      Applied automatically at booking — up to {{ availableCredit | currency: 'USD' : 'symbol' : '1.2-2' }} comes off the card charge.
                    </p>
                  } @else if (availableCredit > 0) {
                    <div
                      class="flex justify-between"
                      [class.text-trinidad]="useCredit"
                      [class.text-muted-text]="!useCredit"
                    >
                      <span class="inline-flex items-center gap-1.5">
                        <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1"
                          >redeem</span
                        >
                        Reward credit
                      </span>
                      <span>{{ useCredit ? '−' + creditApplied : 'Saved for later' }}</span>
                    </div>
                    <label class="flex items-start gap-2 text-[0.65rem] text-muted-text font-body cursor-pointer">
                      <input
                        type="checkbox"
                        [(ngModel)]="useCredit"
                        name="useCredit"
                        class="mt-0.5 w-3.5 h-3.5 accent-trinidad cursor-pointer"
                      />
                      <span>Apply my {{ availableCredit  | currency: 'USD' : 'symbol' : '1.2-2' }} credit to this booking</span>
                    </label>
                  }
                </div>

                <!-- Promo code -->
                @if (!promoApplied) {
                  @if (!promoOpen) {
                    <button
                      type="button"
                      (click)="promoOpen = true"
                      class="text-trinidad text-xs font-bold hover:underline mt-3"
                    >
                      Have a promo code?
                    </button>
                  } @else {
                    <div class="mt-3 flex gap-2">
                      <input aria-label="Promo code"
                        [(ngModel)]="promoCode"
                        name="promoCode"
                        placeholder="Try WELCOME10 or TURF20"
                        class="flex-1 bg-cream/60 border border-dark-text/15 rounded-lg px-3 py-2 text-xs font-body text-dark-text focus:outline-none focus:border-jungle-green uppercase"
                      />
                      <button
                        type="button"
                        (click)="applyPromo()"
                        class="px-4 py-2 rounded-lg bg-trinidad text-white text-xs uppercase tracking-[0.12em] font-button font-bold hover:opacity-95"
                      >
                        Apply
                      </button>
                    </div>
                  }
                }

                <hr class="my-4 border-dark-text/8" />
                <div class="flex justify-between items-baseline">
                  <span class="font-headline font-bold text-dark-text">Total</span>
                  <span class="font-headline font-bold text-trinidad text-3xl">{{ total | currency: 'USD' : 'symbol' : '1.2-2'  }}</span>
                </div>
                @if (selectedPaymentMethod) {
                  <p class="text-[0.65rem] text-muted-text font-body mt-2">
                    Charged {{ chargeDate }} · {{ selectedPaymentMethod.label.split(' ')[0] }} ····{{
                      selectedPaymentMethod.last4
                    }}
                  </p>
                }
              </div>

              <!-- Trust strip -->
              <div class="review-anim bg-jungle-green/5 rounded-2xl border border-jungle-green/15 p-5 space-y-3">
                <!-- todo: add back when we have identity verification -->
                <!-- <div class="flex items-start gap-3">
                  <span aria-hidden="true"
                    class="material-symbols-outlined text-jungle-green shrink-0 mt-0.5"
                    style="font-variation-settings: 'FILL' 1"
                    >verified_user</span
                  >
                  <div>
                    <div class="text-sm font-body font-bold text-dark-text">Verified host</div>
                    <div class="text-xs text-muted-text font-body">Identity, ownership, and insurance checked.</div>
                  </div>
                </div> -->
                <div class="flex items-start gap-3">
                  <span aria-hidden="true"
                    class="material-symbols-outlined text-jungle-green shrink-0 mt-0.5"
                    style="font-variation-settings: 'FILL' 1"
                    >policy</span
                  >
                  <div>
                    <div class="text-sm font-body font-bold text-dark-text">Free cancellation</div>
                    <div class="text-xs text-muted-text font-body">Up to 72 hrs before check-in.</div>
                  </div>
                </div>
                <div class="flex items-start gap-3">
                  <span aria-hidden="true"
                    class="material-symbols-outlined text-jungle-green shrink-0 mt-0.5"
                    style="font-variation-settings: 'FILL' 1"
                    >support_agent</span
                  >
                  <div>
                    <div class="text-sm font-body font-bold text-dark-text">24/7 trip support</div>
                    <div class="text-xs text-muted-text font-body">Reach a real person any time.</div>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>

    <!-- Sticky bottom CTA on phones and tablets (the page is one column until lg) -->
    <div
      class="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-dark-text/10 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex items-center justify-between gap-4"
    >
      <div>
        <div class="text-[0.65rem] font-label uppercase tracking-[0.1em] text-muted-text font-bold">Total</div>
        <div class="font-headline font-bold text-trinidad text-2xl leading-none">{{ total | currency: 'USD' : 'symbol' : '1.2-2' }}</div>
        <div class="text-[0.65rem] text-muted-text font-body">{{ nights }} {{ nights === 1 ? 'night' : 'nights' }}</div>
      </div>
      <button
        type="button"
        (click)="onConfirm($event)"
        [disabled]="submitting || !isFormValid"
        class="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trinidad text-white font-button uppercase tracking-[0.12em] text-xs font-bold hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity shadow-[0_8px_20px_rgba(227,83,13,0.25)]"
      >
        @if (submitting) {
          <span aria-hidden="true" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
          Locking…
        } @else {
          @if (!isFormValid) {
            <span aria-hidden="true" class="material-symbols-outlined text-base">lock</span>
          }
          {{ listing!.reservationProcess === 'instant' ? 'Confirm' : 'Request' }}
        }
      </button>
    </div>
  }
</main>
<!-- todo: add back when we have identity verification  -->
<!-- <cnt-id-verify-modal [open]="idVerifyOpen" (closed)="idVerifyOpen = false" (verified)="onVerified()"></cnt-id-verify-modal> -->

<curbnturf-footer></curbnturf-footer>
